/**
 * Gate 1 — Step 3: Trust Metrics Register.
 *
 * Unlocks after Step 2 has at least one active risk.
 * Canonical = Generated; Derived = Defined (same mapping as Risk Register).
 */

import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Link,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {
  fetchMetricsRegister,
  generateMetricsRegister,
  addMetric,
  updateMetric,
  deleteMetric,
} from "../../services/metricsRegisterClient";
import AddMetricDialog from "./AddMetricDialog";
import ConfigureThresholdDialog from "./ConfigureThresholdDialog";
import DeactivateMetricDialog from "./DeactivateMetricDialog";
import DeleteMetricDialog from "./DeleteMetricDialog";
import ReactivateMetricDialog from "./ReactivateMetricDialog";
import { postGate0FeaturesEnabled } from "../../config/productFeatures";
import AiProvenanceLink from "../../components/AiProvenanceLink";

const POST_GATE0_DISABLED = !postGate0FeaturesEnabled();
/** First-time Generate stays; Regenerate only appends metrics for newly added risks. */
const SHOW_METRICS_REGENERATE = true;

/**
 * Primary borderless states (hover underline, focus ring, active fill).
 * The resting color is passed in so Configure Threshold, Deactivate, Delete,
 * Activate, and Regenerate keep the colors they already use.
 */
function metricBorderlessActionSx(color) {
  return {
    boxSizing: "border-box",
    borderRadius: "4px",
    border: "2px solid transparent",
    bgcolor: "transparent",
    backgroundColor: "transparent",
    backgroundImage: "none",
    boxShadow: "none",
    textDecoration: "none",
    textUnderlineOffset: "2px",
    color,
    "&:hover:not(.Mui-disabled):not(:disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
      bgcolor: "transparent",
      backgroundColor: "transparent",
      backgroundImage: "none",
      border: "2px solid transparent",
      color,
      textDecoration: "underline",
      boxShadow: "none",
    },
    "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
      outline: "none",
      bgcolor: "transparent !important",
      backgroundColor: "transparent !important",
      backgroundImage: "none !important",
      border: "2px solid #005FCC",
      color: `${color} !important`,
      textDecoration: "none",
      boxShadow: "none",
    },
    "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
      bgcolor: "transparent !important",
      backgroundColor: "transparent !important",
      backgroundImage: "none !important",
      border: "2px solid #005FCC",
      color: `${color} !important`,
      textDecoration: "none",
    },
    "&:active:not(.Mui-disabled):not(:disabled)": {
      bgcolor: "#F0F6FF !important",
      backgroundColor: "#F0F6FF !important",
      backgroundImage: "none !important",
      border: "1px solid #005BF0",
      color: `${color} !important`,
      textDecoration: "none",
      boxShadow: "none",
    },
    "&.Mui-disabled, &:disabled": {
      bgcolor: "transparent",
      backgroundColor: "transparent",
      backgroundImage: "none",
      border: "2px solid transparent",
      color: "#BBBBBB",
      textDecoration: "none",
      boxShadow: "none",
      cursor: "not-allowed",
    },
    "& .MuiTouchRipple-root": { display: "none" },
  };
}
const log = (...args) => console.log("[GATE1.STEP3.UI]", ...args);

function sourceLabel(source) {
  return source === "Generated" ? "Canonical" : "Derived";
}

function isCanonical(metric) {
  return metric.source === "Generated";
}

const SEVERITY_DOT = {
  Critical: "var(--Danger-60, #B82500)",
  High: "var(--Danger-40, #FF7152)",
  Medium: "var(--Warning-60, #8A5C00)",
  Low: "var(--Success-60, #00754E)",
};

function StatusDot({ value, colors }) {
  const color = colors[value] || "#98A2B3";
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
      <Box
        component="span"
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          bgcolor: color,
          flexShrink: 0,
        }}
      />
      <Typography
        component="span"
        sx={{
          fontFamily: "Lato, sans-serif",
          fontWeight: 700,
          fontStyle: "bold",
          fontSize: "14px",
          lineHeight: "17.16px",
          letterSpacing: "0.15px",
          color,
        }}
      >
        {value || "—"}
      </Typography>
    </Box>
  );
}

function SourceBadge({ source }) {
  const canonical = source === "Generated";
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: canonical ? 78 : 62,
        height: 24,
        boxSizing: "border-box",
        px: 1,
        borderRadius: "12px",
        fontFamily: "Lato, sans-serif",
        fontWeight: 700,
        fontStyle: "normal",
        fontSize: canonical ? "14px" : "13px",
        lineHeight: "24px",
        letterSpacing: 0,
        textAlign: "center",
        verticalAlign: "middle",
        bgcolor: canonical ? "#F5EEFF" : "#E0F2FE",
        color: canonical
          ? "var(--Accent-Color-60, #9551EC)"
          : "var(--Primary-Dark, #0046B8)",
      }}
    >
      {sourceLabel(source)}
    </Box>
  );
}

function StatDot({ color, label, count, active, onClick }) {
  const interactive = typeof onClick === "function";
  return (
    <Box
      component={interactive ? "button" : "span"}
      type={interactive ? "button" : undefined}
      onClick={onClick}
      aria-pressed={interactive ? active : undefined}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        border: "none",
        bgcolor: "transparent",
        cursor: interactive ? "pointer" : "default",
        p: 0,
        m: 0,
      }}
    >
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color }} />
      <Typography
        component="span"
        sx={{ fontSize: 14, fontWeight: 400, lineHeight: "20px", color: "#555555" }}
      >
        <Box component="span" sx={{ fontWeight: 700, color }}>
          {count}
        </Box>{" "}
        {label}
      </Typography>
    </Box>
  );
}

function formatThreshold(metric) {
  if (metric?.threshold == null || Number.isNaN(Number(metric.threshold))) return "—";
  const n = Number(metric.threshold);
  const dir = metric.direction || "gte";
  const prefix = dir === "lte" ? "≤" : dir === "lt" ? "<" : dir === "eq" ? "=" : "≥";
  return `${prefix} ${n}`;
}

const METRICS_TABLE_BORDER = "1px solid var(--Grey-20, #E6E6E6)";

const thSx = {
  fontFamily: "Lato",
  fontWeight: 700,
  fontStyle: "bold",
  fontSize: "16px",
  color: "var(--Grey-70, #383838)",
  borderBottom: METRICS_TABLE_BORDER,
  bgcolor: "transparent",
  py: 1.5,
  px: 1.5,
};

const tdSx = {
  fontSize: "14px",
  color: "#344054",
  borderBottom: METRICS_TABLE_BORDER,
  py: 1.75,
  px: 1.5,
  verticalAlign: "middle",
};

/** Inset with card content — row lines + left/right rails (same as Risk Register) */
const metricsTableWrapSx = {
  width: "100%",
  overflowX: "auto",
  boxSizing: "border-box",
  borderLeft: METRICS_TABLE_BORDER,
  borderRight: METRICS_TABLE_BORDER,
};

const metricsTableSx = {
  width: "100%",
  minWidth: 980,
  borderCollapse: "collapse",
};

export default function Step3MetricsRegister({
  projectId,
  unlocked,
  lockReason,
  risksRevision = "",
  onMetricsReady,
}) {
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  const [metrics, setMetrics] = useState([]);
  const [risks, setRisks] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [tab, setTab] = useState("Total");
  const [generating, setGenerating] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [configuring, setConfiguring] = useState(null);
  const [configBusy, setConfigBusy] = useState(false);
  const [deactivating, setDeactivating] = useState(null);
  const [deactivatingBusy, setDeactivatingBusy] = useState(false);
  const [activating, setActivating] = useState(null);
  const [activatingBusy, setActivatingBusy] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [deletingBusy, setDeletingBusy] = useState(false);

  const applyPayload = (data) => {
    setMetrics(data?.metrics || []);
    setRisks(data?.risks || []);
    setCatalog(data?.catalog || []);
  };

  const refreshRegister = async ({ quiet = false } = {}) => {
    if (!projectId) return null;
    try {
      if (!quiet) setLoading(true);
      const res = await fetchMetricsRegister(projectId);
      if (res?.success) {
        applyPayload(res.data);
        setLoaded(true);
      }
      return res;
    } catch (e) {
      if (!quiet) {
        setError(e.response?.data?.error || e.message || "Failed to load metrics register.");
      }
      return null;
    } finally {
      if (!quiet) setLoading(false);
    }
  };

  useEffect(() => {
    if (!unlocked) {
      log("locked — skipping GET /metrics-register");
      return;
    }
    log("unlocked / risks changed → GET /metrics-register", {
      projectId,
      risksRevision: risksRevision?.slice?.(0, 80) || risksRevision,
    });
    refreshRegister();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unlocked, projectId, risksRevision]);

  // When parent re-locks, allow a fresh load next unlock
  useEffect(() => {
    if (!unlocked) setLoaded(false);
  }, [unlocked]);

  const openAddMetric = async () => {
    // Always pull latest risks so Step 2 adds show up immediately
    await refreshRegister({ quiet: true });
    setAddOpen(true);
  };
  const visibleOnActiveRisks = useMemo(
    () => metrics.filter((m) => m.riskEnabled !== false),
    [metrics]
  );
  const activeMetrics = useMemo(
    () => visibleOnActiveRisks.filter((m) => m.enabled !== false),
    [visibleOnActiveRisks]
  );
  const deactivatedMetrics = useMemo(
    () => visibleOnActiveRisks.filter((m) => m.enabled === false),
    [visibleOnActiveRisks]
  );

  // Tell the parent when at least one active metric exists — unlocks the
  // "Test Strategy" button on the Gate 1 page.
  useEffect(() => {
    onMetricsReady?.(activeMetrics.length > 0);
  }, [activeMetrics.length, onMetricsReady]);
  const canonical = useMemo(
    () => visibleOnActiveRisks.filter((m) => m.source === "Generated"),
    [visibleOnActiveRisks]
  );
  const derived = useMemo(
    () => visibleOnActiveRisks.filter((m) => m.source === "Defined"),
    [visibleOnActiveRisks]
  );
  const visible =
    tab === "Canonical"
      ? canonical
      : tab === "Derived"
        ? derived
        : tab === "Deactivated"
          ? deactivatedMetrics
          : activeMetrics;
  const showMetricsTable = !loading && visible.length > 0;

  const handleGenerate = async () => {
    if (!unlocked) return;
    try {
      setGenerating(true);
      const res = await generateMetricsRegister(projectId);
      if (!res?.success) {
        setError(res?.error || "Metrics generation failed.");
        return;
      }
      applyPayload(res.data);
      setLoaded(true);
      const generatedCount = res.data.generatedCount || 0;
      const mode = res.data.mode;
      if (mode === "unchanged") {
        setToast("No new risks added. Existing metrics unchanged.");
      } else if (mode === "incremental") {
        setToast(`Generated ${generatedCount} metrics for new risk(s).`);
      } else {
        setToast(`Generated ${generatedCount} metrics.`);
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Metrics generation failed.");
    } finally {
      setGenerating(false);
    }
  };

  const handleAdd = async (payload) => {
    try {
      const res = await addMetric(projectId, payload);
      if (res?.success) {
        applyPayload(res.data);
        setAddOpen(false);
        setToast("Metric added.");
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Failed to add metric.");
    }
  };

  const handleSaveThreshold = async (patch) => {
    if (!configuring?.id) return;
    try {
      setConfigBusy(true);
      const res = await updateMetric(projectId, configuring.id, patch);
      if (res?.success) {
        applyPayload(res.data);
        setConfiguring(null);
        setToast("Threshold updated successfully.");
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Failed to update threshold.");
    } finally {
      setConfigBusy(false);
    }
  };

  const handleConfirmDeactivate = async () => {
    if (!deactivating?.id) return;
    const name = (deactivating.metricName || "").trim() || "Metric";
    try {
      setDeactivatingBusy(true);
      const res = await updateMetric(projectId, deactivating.id, { enabled: false });
      if (res?.success) {
        applyPayload(res.data);
        setDeactivating(null);
        setToast(`${name} successfully deactivated`);
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Failed to deactivate metric.");
    } finally {
      setDeactivatingBusy(false);
    }
  };

  const handleConfirmActivate = async () => {
    if (!activating?.id) return;
    const name = (activating.metricName || "").trim() || "Metric";
    try {
      setActivatingBusy(true);
      const res = await updateMetric(projectId, activating.id, { enabled: true });
      if (res?.success) {
        applyPayload(res.data);
        setActivating(null);
        setToast(`${name} successfully activated`);
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Failed to activate metric.");
    } finally {
      setActivatingBusy(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleting?.id) return;
    const name = (deleting.metricName || "").trim() || "Metric";
    try {
      setDeletingBusy(true);
      const res = await deleteMetric(projectId, deleting.id);
      if (res?.success) {
        applyPayload(res.data);
        setDeleting(null);
        setToast(`${name} successfully deleted`);
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Failed to delete metric.");
    } finally {
      setDeletingBusy(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          bgcolor: "#fff",
          border: "1px solid var(--Grey-20, #E6E6E6)",
          borderRadius: 2,
          p: { xs: 2, sm: 3 },
          mt: 3,
          position: "relative",
          opacity: unlocked ? 1 : 0.55,
          width: "100%",
          minWidth: 0,
          boxSizing: "border-box",
        }}
      >
        {!unlocked && (
          <Tooltip title={lockReason || "Generate risks in Step 2 to unlock Metrics Register"}>
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                bgcolor: "rgba(255,255,255,0.55)",
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 2,
                cursor: "not-allowed",
              }}
            >
              <Box sx={{ textAlign: "center" }}>
                <LockOutlinedIcon sx={{ fontSize: 32, color: "#888" }} />
                <Typography fontWeight={700} color="#555" mt={1}>
                  {lockReason || "Locked until Step 2 risks are generated"}
                </Typography>
              </Box>
            </Box>
          </Tooltip>
        )}

        <Box
          sx={{
            mx: { xs: -2, sm: -3 },
            px: { xs: 2, sm: 3 },
            pb: 2,
            mb: showMetricsTable ? 0 : 2,
            borderBottom: showMetricsTable ? "none" : METRICS_TABLE_BORDER,
          }}
        >
          <Typography
            sx={{
              fontFamily: "Lato, sans-serif",
              fontWeight: 700,
              fontStyle: "normal",
              fontSize: "24px",
              lineHeight: "100%",
              letterSpacing: "0%",
              color: "#383838",
            }}
          >
            Step 3 — Trust Metrics Register
          </Typography>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 400,
              lineHeight: "21px",
              color: "var(--Grey-60, #555555)",
              mt: 0.5,
            }}
          >
            Canonical metrics preserve standardized assurance semantics while derived
            metrics support enterprise customization.
          </Typography>
          <AiProvenanceLink projectId={projectId} steps={["Metrics register"]} refreshKey={generating} sx={{ mt: 1 }} />

          <Box sx={{ display: "flex", gap: 2.5, mt: 1.5, flexWrap: "wrap", alignItems: "center" }}>
            <StatDot
              color="var(--Grey-50, #707070)"
              label="Total"
              count={activeMetrics.length + deactivatedMetrics.length}
              active={tab === "Total"}
              onClick={() => setTab("Total")}
            />
            <StatDot
              color="var(--Accent-Color-60, #9551EC)"
              label="Canonical"
              count={canonical.length}
              active={tab === "Canonical"}
              onClick={() => setTab("Canonical")}
            />
            <StatDot
              color="var(--Primary-Dark, #0046B8)"
              label="Derived"
              count={derived.length}
              active={tab === "Derived"}
              onClick={() => setTab("Derived")}
            />
            <StatDot
              color="var(--Success-60, #00754E)"
              label="Active"
              count={activeMetrics.length}
              active={tab === "Active"}
              onClick={() => setTab("Active")}
            />
            <StatDot
              color="var(--Warning-60, #8A5C00)"
              label="Deactivated"
              count={deactivatedMetrics.length}
              active={tab === "Deactivated"}
              onClick={() => setTab("Deactivated")}
            />
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <CircularProgress size={28} />
          </Box>
        ) : visible.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center", color: "#667085" }}>
            {unlocked ? (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
                <Typography sx={{ fontSize: 13, color: "#667085" }}>
                  {tab === "Deactivated"
                    ? "No deactivated metrics."
                    : tab === "Active" && visibleOnActiveRisks.length > 0
                      ? "No active metrics."
                      : "No metrics yet. Generate from risks or add one manually."}
                </Typography>
                {visibleOnActiveRisks.length === 0 && (
                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", justifyContent: "center" }}>
                  <Button
                    variant="outlined"
                    onClick={handleGenerate}
                    disabled={POST_GATE0_DISABLED || generating}
                    sx={{ textTransform: "none", fontWeight: 600 }}
                  >
                    {generating ? <CircularProgress size={16} /> : "Generate metrics"}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={openAddMetric}
                    disabled={POST_GATE0_DISABLED || generating || activeMetrics.filter((m) => m.source === "Generated").length === 0}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      borderColor: "#005BF0",
                      color: "#005BF0",
                      "&:hover": { borderColor: "#005BF0", bgcolor: "rgba(26,110,245,0.04)" },
                      "&.Mui-disabled": {
                        borderColor: "#D0D5DD",
                        color: "#98A2B3",
                      },
                    }}
                  >
                    Add derived metric
                  </Button>
                </Box>
                )}
              </Box>
            ) : (
              "Unlock Step 2 to continue."
            )}
          </Box>
        ) : (
          <>
            <TableContainer
              sx={{
                ...metricsTableWrapSx,
                borderTop: METRICS_TABLE_BORDER,
              }}
            >
              <Table size="small" sx={metricsTableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={thSx}>Attribute</TableCell>
                    <TableCell sx={thSx}>Risk</TableCell>
                    <TableCell sx={thSx}>Metric</TableCell>
                    <TableCell sx={thSx}>Threshold</TableCell>
                    <TableCell sx={thSx}>Severity</TableCell>
                    <TableCell sx={thSx}>Source</TableCell>
                    <TableCell sx={thSx}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visible.map((m) => {
                    const isActive = m.enabled !== false;
                    return (
                    <TableRow
                      key={m.id}
                      hover
                      sx={!isActive ? { bgcolor: "rgba(138, 92, 0, 0.06)" } : undefined}
                    >
                      <TableCell
                        sx={{
                          ...tdSx,
                          fontFamily: "Lato, sans-serif",
                          fontWeight: 700,
                          fontStyle: "normal",
                          fontSize: "16px",
                          lineHeight: "20.02px",
                          letterSpacing: "0.15px",
                          color: "var(--Grey-60, #555555)",
                        }}
                      >
                        {m.attribute || "—"}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...tdSx,
                          fontFamily: "Lato, sans-serif",
                          fontWeight: 600,
                          fontStyle: "normal",
                          fontSize: "14px",
                          lineHeight: "18.59px",
                          letterSpacing: "0.14px",
                          color: "var(--Grey-60, #555555)",
                        }}
                      >
                        {m.riskName || "—"}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...tdSx,
                          fontFamily: "Lato, sans-serif",
                          fontWeight: 600,
                          fontStyle: "normal",
                          fontSize: "14px",
                          lineHeight: "18.59px",
                          letterSpacing: "0.14px",
                          color: "var(--Grey-60, #555555)",
                        }}
                      >
                        {m.metricName}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...tdSx,
                          fontFamily: "Lato, sans-serif",
                          fontWeight: 700,
                          fontStyle: "normal",
                          fontSize: "14px",
                          lineHeight: "18.59px",
                          letterSpacing: "0.14px",
                          color: "var(--Grey-60, #555555)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatThreshold(m)}
                      </TableCell>
                      <TableCell sx={tdSx}>
                        <StatusDot value={m.severity} colors={SEVERITY_DOT} />
                      </TableCell>
                      <TableCell sx={tdSx}>
                        <SourceBadge source={m.source} />
                      </TableCell>
                      <TableCell sx={{ ...tdSx, fontSize: "13px", whiteSpace: "nowrap" }}>
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 1,
                            flexWrap: "nowrap",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {!isActive ? (
                            <Link
                              component="button"
                              type="button"
                              underline="none"
                              onClick={() => setActivating(m)}
                              sx={{
                                fontFamily: "Lato, sans-serif",
                                fontWeight: 700,
                                fontSize: "13px",
                                lineHeight: "24px",
                                whiteSpace: "nowrap",
                                ...metricBorderlessActionSx("#00754E"),
                              }}
                            >
                              Activate
                            </Link>
                          ) : (
                            <>
                              <Link
                                component="button"
                                type="button"
                                underline="none"
                                onClick={() => setConfiguring(m)}
                                sx={{
                                  fontFamily: "Lato, sans-serif",
                                  fontWeight: 700,
                                  fontStyle: "normal",
                                  fontSize: "13px",
                                  lineHeight: "24px",
                                  letterSpacing: "0%",
                                  verticalAlign: "middle",
                                  whiteSpace: "nowrap",
                                  ...metricBorderlessActionSx("#005BF0"),
                                }}
                              >
                                Configure Threshold
                              </Link>
                              {isCanonical(m) ? (
                                <Link
                                  component="button"
                                  type="button"
                                  underline="none"
                                  onClick={() => setDeactivating(m)}
                                  sx={{
                                    fontFamily: "Lato, sans-serif",
                                    fontWeight: 700,
                                    fontStyle: "normal",
                                    fontSize: "13px",
                                    lineHeight: "24px",
                                    letterSpacing: "0%",
                                    verticalAlign: "middle",
                                    whiteSpace: "nowrap",
                                    ...metricBorderlessActionSx("#555555"),
                                  }}
                                >
                                  Deactivate
                                </Link>
                              ) : (
                                <Link
                                  component="button"
                                  type="button"
                                  underline="none"
                                  onClick={() => setDeleting(m)}
                                  sx={{
                                    fontFamily: "Lato, sans-serif",
                                    fontWeight: 700,
                                    fontStyle: "normal",
                                    fontSize: "13px",
                                    lineHeight: "24px",
                                    letterSpacing: "0%",
                                    verticalAlign: "middle",
                                    whiteSpace: "nowrap",
                                    ...metricBorderlessActionSx("#DE350B"),
                                  }}
                                >
                                  Delete
                                </Link>
                              )}
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, gap: 1.5 }}>
              {SHOW_METRICS_REGENERATE && activeMetrics.length > 0 && (
                <Button
                  variant="text"
                  disableRipple
                  onClick={handleGenerate}
                  disabled={POST_GATE0_DISABLED || generating}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    ...metricBorderlessActionSx("#005BF0"),
                  }}
                >
                  {generating ? <CircularProgress size={16} /> : "Regenerate"}
                </Button>
              )}
              <Button
                variant="outlined"
                onClick={openAddMetric}
                disabled={POST_GATE0_DISABLED || !risks.length}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderColor: "#005BF0",
                  color: "#005BF0",
                  "&:hover": { borderColor: "#005BF0", bgcolor: "rgba(26,110,245,0.04)" },
                }}
              >
                Add Metric
              </Button>
            </Box>
          </>
        )}
      </Box>

      <AddMetricDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSave={handleAdd}
        risks={risks}
        catalog={catalog}
        metrics={metrics}
      />
      <ConfigureThresholdDialog
        metric={configuring}
        onClose={() => setConfiguring(null)}
        onSave={handleSaveThreshold}
        submitting={configBusy}
      />
      <DeactivateMetricDialog
        metric={deactivating}
        onClose={() => {
          if (!deactivatingBusy) setDeactivating(null);
        }}
        onConfirm={handleConfirmDeactivate}
        submitting={deactivatingBusy}
      />
      <ReactivateMetricDialog
        metric={activating}
        onClose={() => {
          if (!activatingBusy) setActivating(null);
        }}
        onConfirm={handleConfirmActivate}
        submitting={activatingBusy}
      />
      <DeleteMetricDialog
        metric={deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleConfirmDelete}
        submitting={deletingBusy}
      />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          severity="success"
          icon={<CheckCircleOutlineIcon fontSize="inherit" />}
          action={
            <IconButton size="small" color="inherit" onClick={() => setToast("")}>
              <CloseIcon fontSize="small" />
            </IconButton>
          }
          sx={{
            bgcolor: "#101828",
            color: "#fff",
            alignItems: "center",
            "& .MuiAlert-icon": { color: "#32D583" },
          }}
        >
          {toast}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={6000}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
}
